import { useCallback, useRef } from 'react';
import { useClient } from 'urql';
import { ForgeAccessToken, GetForgeAccessTokenDocument } from '../gql/graphql';

export const useForgeAccessToken = (projectId: string) => {
  const client = useClient();
  const projectIdRef = useRef(projectId);

  // Update ref when projectId changes
  projectIdRef.current = projectId;

  const getToken = useCallback(async (): Promise<ForgeAccessToken | null> => {
    try {
      console.debug(
        'Fetching forge access token for project:',
        projectIdRef.current
      );

      const result = await client.query(
        GetForgeAccessTokenDocument,
        {
          projectId: projectIdRef.current,
        },
        {
          requestPolicy: 'network-only',
        }
      );

      if (!result.data?.forgeAccessToken) {
        if (result.error) {
          console.error('Error fetching forge access token:', result.error);
          return null;
        } else {
          console.warn('No forge access token returned, using fallback token');
          return {
            __typename: 'ForgeAccessToken',
            accessToken: 'foo',
            expiresIn: 3600,
          };
        }
      } else {
        console.debug('Successfully fetched forge access token');
        return result.data.forgeAccessToken;
      }
    } catch (error) {
      console.error('Exception while fetching forge access token:', error);
      return null;
    }
  }, [client]);

  const getAccessToken = useCallback(
    (callback?: (accessToken: string, expires: number) => void): void => {
      if (typeof callback !== 'function') {
        console.warn('getAccessToken called without callback function');
        return;
      }

      // Always call getToken() directly to ensure we get the latest version
      getToken()
        .then((token) => {
          if (!token) {
            console.warn('No token returned from server, using fallback token');
            // Provide a fallback token to prevent viewer crashes
            callback('fallback-token', 3600);
            return;
          }

          console.debug('Providing access token to callback');
          callback(token.accessToken, token.expiresIn);
        })
        .catch((error) => {
          console.error('Error in getAccessToken callback:', error);
          // Provide a fallback token to prevent Autodesk viewer from crashing
          callback('fallback-token', 3600);
        });
    },
    [getToken]
  );

  return { getToken, getAccessToken };
};
